import { useEffect, useRef } from 'react';

type EventListenerHook = {
  <E extends keyof HTMLElementEventMap>(
    eventName: E,
    listener: (event: HTMLElementEventMap[E]) => void,
    target: HTMLElement | undefined
  ): void;
  <E extends keyof DocumentEventMap>(
    eventName: E,
    listener: (event: DocumentEventMap[E]) => void,
    target: Document | undefined
  ): void;
  <E extends keyof WindowEventMap>(
    eventName: E,
    listener: (event: WindowEventMap[E]) => void,
    target: Window | undefined
  ): void;
  <E extends keyof CustomEvent>(
    eventName: string,
    listener: (event: E) => void,
    target: EventTarget | undefined
  ): void;
};

export const useEventListener: EventListenerHook = (
  eventName: string,
  listener: EventListener,
  target: EventTarget | undefined
) => {
  const listenerRef = useRef<EventListener>();

  useEffect(() => {
    listenerRef.current = listener;
  }, [listener]);

  useEffect(() => {
    if (!(target && 'addEventListener' in target)) {
      return;
    }

    const currentListener: EventListener = e => listenerRef.current?.(e);

    target.addEventListener(eventName, currentListener);

    return () => {
      target.removeEventListener(eventName, currentListener);
    };
  }, [eventName, target]);
};
